import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Plus, 
  Search, 
  Building2, 
  MapPin, 
  DollarSign,
  ArrowRight,
  Loader2,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const ProjectsListPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter]);

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) ||
        p.address.toLowerCase().includes(term) ||
        p.city.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    setFilteredProjects(filtered);
  };

  const getStatusBadge = (status) => {
    const styles = {
      planning: 'status-planning',
      'in-progress': 'status-in-progress',
      completed: 'status-completed',
      sold: 'status-sold',
    };
    return styles[status] || 'bg-secondary text-secondary-foreground';
  };

  const calculateProgress = (project) => {
    if (!project.target_budget || project.target_budget === 0) return 0;
    return Math.min(100, Math.round((project.total_spent / project.target_budget) * 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold font-heading tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">
            Manage your fix & flip investments
          </p>
        </div>
        <Link to="/projects/new">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm btn-scale" data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-input/50 border-border focus:border-primary rounded-sm h-10"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-[180px] bg-input/50 border-border rounded-sm h-10" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border rounded-sm">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planning">Planning</SelectItem>
            <SelectItem value="in-progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project, index) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className={`animate-fade-in stagger-${(index % 4) + 1}`}
              data-testid={`project-card-${project.id}`}
            >
              <Card className="border-border bg-card rounded-sm card-hover h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <Badge variant="outline" className={`rounded-sm font-mono text-xs uppercase ${getStatusBadge(project.status)}`}>
                      {project.status}
                    </Badge>
                  </div>
                  
                  <h3 className="font-bold text-lg mb-2 font-heading">{project.name}</h3>
                  
                  <div className="flex items-center text-sm text-muted-foreground mb-4">
                    <MapPin className="w-4 h-4 mr-1" />
                    {project.city}, {project.state}
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Purchase Price</span>
                      <span className="font-mono font-medium">{formatCurrency(project.purchase_price)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Budget Spent</span>
                      <span className="font-mono font-medium">{formatCurrency(project.total_spent)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Estimated ARV</span>
                      <span className="font-mono font-medium text-green-400">{formatCurrency(project.estimated_arv)}</span>
                    </div>
                  </div>

                  {/* Budget Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Budget Progress</span>
                      <span className="font-mono">{calculateProgress(project)}%</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          calculateProgress(project) > 90 ? 'bg-red-500' :
                          calculateProgress(project) > 70 ? 'bg-amber-500' : 'bg-primary'
                        }`}
                        style={{ width: `${calculateProgress(project)}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">View details</span>
                    <ArrowRight className="w-4 h-4 text-primary" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-xl font-bold mb-2">
              {searchTerm || statusFilter !== 'all' ? 'No projects found' : 'No projects yet'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your filters'
                : 'Start tracking your first fix & flip investment'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Link to="/projects/new">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Project
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProjectsListPage;
